import { useState } from "react";
import { Download } from "lucide-react";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useData } from "@/context/DataContext";
import { FinTable } from "@/components/FinTable";
import { KpiCard, Panel, PageHeader, ChartTip } from "@/components/Widgets";
import { CashCharts } from "@/pages/ChartPages";
import { cut, downloadCsv, fmt, fmtCr, sum, last } from "@/lib/api";

const GROUPS = [["institutions", "Institutions"], ["corporates", "Corporates"], ["central", "Central / Head Office"], ["other", "Other"]];
const ROLE = { entity: "unit sheet", other_entity: "NCF input", inter_unit_funding: "inter-unit funding", central_expense: "central cost" };

const entityCfRows = (e, h) => {
  const s = (k) => cut(e.series[k], h);
  return [
    { key: "e-eb", label: "Operating Surplus (EBITDA)", values: s("ebitda"), type: "subtotal" },
    { key: "e-uc", label: "Unit capex", values: s("unit_capex"), indent: 1 },
    { key: "e-ie", label: "Interest expense", values: s("interest_expense"), indent: 1 },
    { key: "e-ii", label: "Interest income", values: s("interest_income"), indent: 1 },
    { key: "e-pc", label: "Project capex", values: s("project_capex"), indent: 1 },
    { key: "e-sapc", label: "Surplus after project capex", values: s("surplus_after_project_capex"), type: "subtotal" },
    { key: "e-dd", label: "Loan drawdown", values: s("drawdown"), indent: 1 },
    { key: "e-rp", label: "Loan repayment", values: s("repayment"), indent: 1 },
    { key: "e-ncf", label: "Net Cash Flow", values: s("ncf"), type: "total" },
    { key: "e-sw", label: `Contribution to group NCF (switch = ${e.switch})`, values: s("ncf").map((v) => v * (e.switch || 0)), type: "subtotal" },
  ];
};

const EntityCfKpis = ({ e, h, years }) => {
  const s = (k) => sum(cut(e.series[k], h));
  return (
    <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="cf-entity-kpis">
      <KpiCard testid="cf-kpi-ebitda" label="EBITDA (horizon)" value={fmtCr(s("ebitda"))} tone="pos" />
      <KpiCard testid="cf-kpi-capex" label="Capex (horizon)" value={fmtCr(s("unit_capex") + s("project_capex"))} tone="amber" />
      <KpiCard testid="cf-kpi-debt-service" label="Debt service (horizon)" value={fmtCr(s("interest_expense") + s("repayment"))} tone="neg" sub={`Drawdowns ${fmtCr(s("drawdown"))}`} />
      <KpiCard testid="cf-kpi-entity-ncf" label={`Net cash flow FY ${years[0]}–${last(years)}`} value={fmtCr(s("ncf"))} tone={s("ncf") >= 0 ? "pos" : "neg"} sub={e.switch ? "Included in group NCF" : "Switched off in group NCF"} />
    </div>
  );
};

const buildRows = (ncf, h, hideZero) => {
  const rows = [];
  GROUPS.forEach(([g, label]) => {
    const lines = ncf.lines.filter((l) => l.group === g);
    if (!lines.length) return;
    rows.push({ key: `h-${g}`, label, type: "section" });
    lines.forEach((l, i) => {
      const v = cut(l.values, h);
      if (hideZero && v.every((x) => Math.abs(x) < 0.005)) return;
      const meta = [l.switch != null ? `sw ${l.switch}` : null, l.incr != null ? `incr ${(l.incr * 100).toFixed(0)}%` : null, ROLE[l.role]].filter(Boolean).join(" · ");
      rows.push({ key: `${g}-${i}`, label: l.label, values: v, indent: 1, meta });
    });
    const gt = ncf.totals.group_totals?.[g];
    if (gt) rows.push({ key: `t-${g}`, label: `Total ${label}`, values: cut(gt, h), type: "subtotal" });
    if (g === "corporates" && ncf.totals.inst_corp_total)
      rows.push({ key: "t-ic", label: "Total (Institutions + Corporates)", values: cut(ncf.totals.inst_corp_total, h), type: "subtotal" });
  });
  const t = ncf.totals;
  if (t.grand_total) rows.push({ key: "gt", label: "Grand Total (Group NCF)", values: cut(t.grand_total, h), type: "total" });
  if (t.opening) rows.push({ key: "op", label: "Opening Funds", values: cut(t.opening, h), noTotal: true });
  if (t.closing) rows.push({ key: "cl", label: "Closing Funds", values: cut(t.closing, h), type: "total", noTotal: true });
  return rows;
};

export default function CashFlow() {
  const { data, horizon, filters } = useData();
  const fe = data.model.entities.find((x) => x.code === filters.entity);
  const [hideZero, setHideZero] = useState(false);
  const { ncf } = data.model;
  const g = data.group;
  const years = cut(data.model.years, horizon);
  const rows = fe ? entityCfRows(fe, horizon) : buildRows(ncf, horizon, hideZero);
  const gt = ncf.totals.group_totals || {};
  const chart = years.map((y, i) => ({
    y, inst: cut(gt.institutions || [], horizon)[i] || 0, corp: cut(gt.corporates || [], horizon)[i] || 0,
    central: cut(g.inter_unit_funding, horizon)[i] + cut(g.central_expense, horizon)[i], closing: cut(g.closing, horizon)[i],
  }));
  return (
    <div data-testid="cash-flow-page">
      <PageHeader kicker={fe ? `Entity cash flow · sheet "${fe.sheet}"` : "NCF sheet · group consolidation"} title={fe ? `Cash Flow - ${fe.short}` : "Cash Flow"} />
      <div className="mb-6"><CashCharts /></div>
      {fe ? <EntityCfKpis e={fe} h={horizon} years={years} /> : (
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="cf-kpi-opening" label={`Opening funds FY ${years[0]}`} value={fmtCr(cut(g.opening, horizon)[0])} />
        <KpiCard testid="cf-kpi-funding" label="Inter-unit funding (horizon)" value={fmtCr(sum(cut(g.inter_unit_funding, horizon)))} tone="blue" sub="e.g. IST-HO funding to group" />
        <KpiCard testid="cf-kpi-central" label="Central / HO costs (horizon)" value={fmtCr(sum(cut(g.central_expense, horizon)))} tone="neg" />
        <KpiCard testid="cf-kpi-closing" label={`Closing funds FY ${last(years)}`} value={fmtCr(last(cut(g.closing, horizon)))} tone={last(cut(g.closing, horizon)) >= 0 ? "pos" : "neg"} />
      </div>
      )}
      {!fe && (
      <Panel className="mt-6" title="Cash flow composition" subtitle="Institutions, corporates and central flows vs closing funds" testid="cf-chart">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={chart} stackOffset="sign">
            <CartesianGrid vertical={false} />
            <XAxis dataKey="y" tickLine={false} axisLine={false} />
            <YAxis tickLine={false} axisLine={false} tickFormatter={fmt} width={60} />
            <Tooltip content={<ChartTip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <ReferenceLine y={0} stroke="#475569" />
            <Bar dataKey="inst" name="Institutions" stackId="s" fill="#3B82F6" />
            <Bar dataKey="corp" name="Corporates" stackId="s" fill="#F43F5E" />
            <Bar dataKey="central" name="Central (net)" stackId="s" fill="#94A3B8" />
            <Line dataKey="closing" name="Closing funds" stroke="#F59E0B" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </Panel>
      )}
      <Panel className="mt-6" title={fe ? `${fe.name} — cash-flow build-up` : "Net Cash Flow statement"} subtitle={fe ? "From the entity sheet; group cash balance is held centrally" : "sw = NCF switch · incr = escalation"} testid="cf-table-panel"
        actions={<>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <Switch checked={hideZero} onCheckedChange={setHideZero} data-testid="cf-hide-zero-toggle" /> Hide zero rows
          </label>
          <Button size="sm" variant="outline" className="border-slate-700" data-testid="export-cash-flow-csv-button"
            onClick={() => downloadCsv("group_ncf.csv", years, rows)}><Download className="mr-1.5 h-3.5 w-3.5" />CSV</Button>
        </>}>
        <FinTable years={years} rows={rows} testid="cf-fin-table" />
      </Panel>
    </div>
  );
}
